import { useCallback, useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router";
import _ from "lodash";

import { ArrowDropDownIcon, ArrowRightIcon } from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { useCaching } from "utils/common";

import Box from "components/Box";
import Collapse from "components/Collapse";
import IconButton from "components/IconButton";
import Stack from "components/Stack";
import Text from "components/Text";
import toast from "components/Toast";

import FieldUpdateIcon from "entities/Field/components/FieldUpdateIcon";
import FieldWrapper from "entities/Package/components/FieldWrapper";
import Instructions from "entities/Package/components/Instructions";
import MenuButton from "entities/Package/components/Section/MenuButton";
import {
  fieldMarkAsSeenBulk,
  IPackageSection,
  useCompanyPackageWithTabsInformation,
} from "entities/Package/sdk";

interface ISection {
  section: IPackageSection;
  categoryId: number;
  isExpanded: boolean;
  disableEdits: boolean;
  updatePackage: ({ message }: { message?: string }) => void;
  dataTestid?: string;
  isFocused?: boolean;
}

const Section = ({
  section,
  categoryId,
  isExpanded,
  updatePackage,
  dataTestid,
  disableEdits,
  isFocused = false,
}: ISection) => {
  const [open, setOpen] = useCaching(isExpanded);

  const [searchParams] = useSearchParams();

  const params = useParams<{
    dealId: string;
    packageId: string;
    tabId: string;
  }>();

  const dealId = Number(params.dealId);
  const packageId = Number(params.packageId);

  const selectedFieldId = useMemo(() => {
    const fieldId = searchParams.get("fieldId");
    return !_.isNil(fieldId) ? Number(fieldId) : null;
  }, [searchParams]);

  useEffect(() => {
    if (!_.isNil(selectedFieldId) && !open) {
      const fieldIsInCategory = section.fields.some(
        (field) => field.field_id === selectedFieldId
      );

      setOpen(fieldIsInCategory);
    }
  }, [selectedFieldId, section, open, setOpen]);

  const { mutate: updateTabs } = useCompanyPackageWithTabsInformation({
    dealId,
    packageId,
  });

  const handleUpdateField = useCallback(
    ({ message }: { message?: string }) => {
      if (message) {
        toast.successMessage(message);
      }
      updatePackage({});
      updateTabs();
    },
    [updatePackage, updateTabs]
  );

  const areAllNA = useMemo(
    () => _.every(section.fields, { n_a_status: true }),
    [section]
  );

  const handleAllMarksAsSeen = useCallback(() => {
    fieldMarkAsSeenBulk({
      dealId,
      packageId,
      fieldIds: section.fields.map((field) => field.field_id),
    }).then(() => {
      updateTabs();
      updatePackage({
        message: `Updated badges for ${section?.name} have been removed successfully!`,
      });
    });
  }, [dealId, section, updatePackage, packageId, updateTabs]);

  return (
    <Box id={section.section_id.toString()}>
      <Box
        sx={{
          padding: theme.spacing(0.5, 1),
          backgroundColor: isFocused ? colors.green10 : colors.blue10,
          borderRadius: "4px",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
          data-testid={dataTestid}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            minHeight="32px"
          >
            <IconButton
              onClick={() => setOpen(!open)}
              size="small"
              dataTestid="dd-section-expand-button"
            >
              {open ? <ArrowDropDownIcon /> : <ArrowRightIcon />}
            </IconButton>
            <Text
              variant="text2"
              fontWeight="500"
              sx={{
                opacity: areAllNA ? 0.5 : 1,
              }}
              data-testid="dd-section-name"
            >
              {section.name}
            </Text>
            {section.updated_field_count > 0 && (
              <FieldUpdateIcon
                tooltip={`Section has ${section.updated_field_count} field${section.updated_field_count > 1 ? "s" : ""} updated`}
                numberOfUpdates={section.updated_field_count}
              />
            )}
          </Stack>
          <Stack>
            <MenuButton
              section={section}
              categoryId={categoryId}
              dealId={dealId}
              updatePackage={updatePackage}
              menuButtonDataTestid="dd-section-actions-button"
              handleAllMarksAsSeen={handleAllMarksAsSeen}
            />
          </Stack>
        </Stack>
        <Instructions
          internalInstructions={section?.internal_instructions}
          externalInstructions={section?.external_instructions}
        />
      </Box>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box
          sx={{ padding: theme.spacing(1, 0, 1, 6) }}
          data-testid="dd-section-fields"
          data-test-id={section.name}
        >
          {_.uniqBy(section.fields, "field_id").map((field) => (
            <FieldWrapper
              key={field.field_id}
              field={field}
              categoryId={categoryId}
              sectionId={section.section_id}
              onUpdate={handleUpdateField}
              disableEdits={disableEdits}
              companySectionAutomaticFillStatus={
                section.latest_company_section_automatic_fill_status
              }
              companySectionAutomaticFillId={
                section.latest_company_section_automatic_fill_id
              }
              companySectionAutomaticFillFileIds={
                section.latest_company_section_automatic_fill_file_ids
              }
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default Section;
